import { useState, useEffect, useCallback, useSyncExternalStore } from "react";
import { markServingCache } from "./useNetworkStatus";

interface CacheEntry<T> {
  data: T;
  ts: number;
  promise: Promise<T> | null;
}

const GLOBAL_CACHE = new Map<string, CacheEntry<unknown>>();
const SUBSCRIBERS = new Map<string, Set<() => void>>();

function notify(key: string) {
  SUBSCRIBERS.get(key)?.forEach((cb) => cb());
}

function subscribe(key: string, cb: () => void) {
  let set = SUBSCRIBERS.get(key);
  if (!set) {
    set = new Set();
    SUBSCRIBERS.set(key, set);
  }
  set.add(cb);
  return () => set?.delete(cb);
}

function getSnapshot<T>(key: string): T | undefined {
  return GLOBAL_CACHE.get(key)?.data as T | undefined;
}

function isStale(ts: number, ttlMs: number): boolean {
  return Date.now() - ts > ttlMs;
}

export interface CachedQueryOptions<T> {
  key: string;
  query: () => Promise<T>;
  ttlMs?: number;
  staleWhileRevalidate?: boolean;
  enabled?: boolean;
}

export interface CachedQueryResult<T> {
  data: T;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  invalidated: boolean;
}

export function useCachedQuery<T>({
  key,
  query,
  ttlMs = 30000,
  staleWhileRevalidate = true,
  enabled = true,
}: CachedQueryOptions<T>): CachedQueryResult<T> {
  const [refreshing, setRefreshing] = useState(false);
  const dataRef = useSyncExternalStore(
    (cb) => subscribe(key, cb),
    () => getSnapshot<T>(key),
    () => getSnapshot<T>(key)
  );

  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    invalidated: boolean;
  }>(() => {
    const cached = GLOBAL_CACHE.get(key);
    return {
      loading: !cached && enabled,
      error: null,
      invalidated: false,
    };
  });

  const queryRef = useCallback(async () => query(), [query]);

  const runQuery = useCallback(
    async (force = false) => {
      const cached = GLOBAL_CACHE.get(key);
      const stale = cached ? isStale(cached.ts, ttlMs) : true;

      if (!force && cached && !stale) {
        setState({ loading: false, error: null, invalidated: false });
        markServingCache(false);
        return;
      }

      if (cached?.promise) {
        if (staleWhileRevalidate) {
          setState((prev) => ({ ...prev, invalidated: true }));
        }
        try {
          await cached.promise;
        } catch {
          // handled by inflight
        }
        return;
      }

      const promise = queryRef();
      if (cached) cached.promise = promise as Promise<unknown>;

      if (staleWhileRevalidate && cached) {
        setState({ loading: false, error: null, invalidated: true });
        markServingCache(true);
      } else {
        setState({ loading: true, error: null, invalidated: false });
        markServingCache(true);
      }

      try {
        const result = await promise;
        GLOBAL_CACHE.set(key, { data: result, ts: Date.now(), promise: null });
        notify(key);
        setState({ loading: false, error: null, invalidated: false });
      } catch (e) {
        const msg = e instanceof Error ? e.message : typeof e === "string" ? e : "Failed to load";
        if (cached) {
          GLOBAL_CACHE.set(key, { ...cached, promise: null });
        }
        setState((prev) => ({ ...prev, loading: false, error: msg }));
      }
    },
    [key, ttlMs, staleWhileRevalidate, queryRef]
  );

  useEffect(() => {
    if (!enabled) return;
    const cached = GLOBAL_CACHE.get(key);
    const stale = cached ? isStale(cached.ts, ttlMs) : true;
    if (!cached || stale) {
      runQuery(false);
    }
  }, [key, enabled, ttlMs, runQuery]);

  const refresh = useCallback(async () => {
    await runQuery(true);
  }, [runQuery]);

  const actualLoading = state.loading && dataRef === undefined;

  return {
    data: dataRef as T,
    loading: actualLoading,
    error: state.error,
    refresh,
    invalidated: state.invalidated,
  };
}

export function invalidateCache(keyPrefix?: string) {
  if (keyPrefix) {
    for (const key of GLOBAL_CACHE.keys()) {
      if (key.startsWith(keyPrefix)) {
        GLOBAL_CACHE.delete(key);
        notify(key);
      }
    }
  } else {
    GLOBAL_CACHE.clear();
    for (const [, set] of SUBSCRIBERS) {
      set.forEach((cb) => cb());
    }
  }
}